import { useEffect, useRef, useState } from "react";

export default function useLobbyQueueStatus({
    basePath,
    fetchJsonWithDiagnostics,
    lobbyState,
    onStopSearch,
    resolvedUserSteamId,
    routePath,
    user,
}) {
    const [queueClosedInfo, setQueueClosedInfo] = useState({
        queueClosed: false,
        updatedAtMs: null,
        schedule: null,
        error: null,
    });
    const [scheduleCountdown, setScheduleCountdown] = useState("");
    const queueClosedStopRef = useRef(false);
    const onStopSearchRef = useRef(onStopSearch);

    useEffect(() => {
        onStopSearchRef.current = onStopSearch;
    }, [onStopSearch]);

    useEffect(() => {
        let cancelled = false;

        const countryParam = user?.country
            ? `?country=${encodeURIComponent(user.country)}`
            : "";

        const loadQueueStatus = async () => {
            try {
                const result = await fetchJsonWithDiagnostics(
                    `${basePath}/api/v1/matchmaking/queue/status${countryParam}`,
                    {
                        credentials: "include",
                    },
                    {
                        context: "Lobby queue status",
                        extra: {
                            route: routePath || null,
                            userId: user?.userId || null,
                            steamId: resolvedUserSteamId,
                        },
                    },
                );
                const data = result.data || {};
                if (!result.ok || data?.ok === false) {
                    throw new Error(
                        result.error ||
                        data?.err ||
                        data?.message ||
                        "Failed to load queue status",
                    );
                }
                if (cancelled) return;
                if (process.env.NODE_ENV !== "production") console.log("[LobbyPage] Queue status payload", {
                    queueClosed: data.queueClosed,
                    schedule: data.schedule || null,
                    country: user?.country || null,
                });
                setQueueClosedInfo({
                    queueClosed: data.queueClosed === true,
                    updatedAtMs:
                        typeof data.updatedAtMs === "number" ? data.updatedAtMs : null,
                    schedule: data.schedule || null,
                    error: null,
                });
            } catch (e) {
                if (cancelled) return;
                setQueueClosedInfo((prev) => ({
                    ...prev,
                    error: e?.message || String(e),
                }));
            }
        };

        loadQueueStatus();
        const pid = setInterval(loadQueueStatus, 10000);
        return () => {
            cancelled = true;
            clearInterval(pid);
        };
    }, [
        basePath,
        fetchJsonWithDiagnostics,
        resolvedUserSteamId,
        routePath,
        user?.userId,
        user?.country,
    ]);

    const isQueueEffectivelyClosed =
        queueClosedInfo.queueClosed ||
        (queueClosedInfo.schedule?.enabled &&
            !queueClosedInfo.schedule?.currentlyOpen);

    useEffect(() => {
        if (
            isQueueEffectivelyClosed &&
            lobbyState === "searching" &&
            !queueClosedStopRef.current
        ) {
            queueClosedStopRef.current = true;
            try {
                onStopSearchRef.current?.();
            } catch (e) {
                // ignore
            }
        }
        if (!isQueueEffectivelyClosed) {
            queueClosedStopRef.current = false;
        }
    }, [isQueueEffectivelyClosed, lobbyState]);

    useEffect(() => {
        const schedule = queueClosedInfo.schedule;
        if (
            !schedule?.enabled ||
            schedule?.currentlyOpen ||
            schedule?.opensAtUTC == null
        ) {
            setScheduleCountdown("");
            return;
        }

        const computeCountdown = () => {
            const now = new Date();
            const openTarget = new Date(now);
            openTarget.setUTCHours(schedule.opensAtUTC, 0, 0, 0);
            if (openTarget <= now) openTarget.setDate(openTarget.getDate() + 1);

            const diffMs = openTarget - now;
            const totalMin = Math.max(0, Math.floor(diffMs / 60000));
            const h = Math.floor(totalMin / 60);
            const m = totalMin % 60;
            return h > 0 ? `${h}h ${m}m` : `${m}m`;
        };

        setScheduleCountdown(computeCountdown());
        const tid = setInterval(
            () => setScheduleCountdown(computeCountdown()),
            60000,
        );
        return () => clearInterval(tid);
    }, [queueClosedInfo.schedule]);

    return {
        queueClosedInfo,
        scheduleCountdown,
        isQueueEffectivelyClosed,
    };
}
